class FdbMemberSignup extends HTMLElement {
  static LOG_PREFIX = '[FDB Medlem Signup]';

  connectedCallback() {
    this._abort = new AbortController();
    this._bind();
    this._debug('init', this._debugContext());
  }

  disconnectedCallback() {
    this._abort?.abort();
    this._debug('disconnected');
  }

  _isDebug() {
    return this.dataset.debug === 'true';
  }

  _debug(step, data) {
    if (!this._isDebug()) return;
    if (data !== undefined) {
      console.log(FdbMemberSignup.LOG_PREFIX, step, data);
    } else {
      console.log(FdbMemberSignup.LOG_PREFIX, step);
    }
  }

  _debugContext() {
    return {
      formMode: this.dataset.formMode || 'signup',
      webhook: (this.dataset.webhook || '').trim() || null,
      designMode: this._isDesignMode(),
      loggedIn: this.dataset.loggedIn === 'true',
      successUrl: (this.dataset.successUrl || '').trim() || null,
      bound: Boolean(this._bound),
      hasForm: Boolean(this._form),
      hasButton: Boolean(this._btn)
    };
  }

  _fieldLabel(input) {
    if (!input) return 'unknown';
    return input.getAttribute('data-field') || input.id || input.name || 'unknown';
  }

  _bind() {
    if (this._bound) return;
    this._form = this.querySelector('[data-form]');
    this._btn = this.querySelector('[data-submit]');
    this._btnText = this.querySelector('[data-btn-text]');
    this._summary = this.querySelector('[data-summary]');
    this._successPanel = this.querySelector('[data-success-panel]');

    if (!this._form || !this._btn || !this._btnText) {
      this._debug('bind:missing-elements', {
        hasForm: Boolean(this._form),
        hasButton: Boolean(this._btn),
        hasButtonText: Boolean(this._btnText)
      });
      return;
    }

    this._bound = true;
    const opts = { signal: this._abort.signal };
    this._onSubmitHandler = (e) => this._onSubmit(e);
    this._form.addEventListener('submit', this._onSubmitHandler, opts);

    if (this.dataset.loggedIn === 'true') this._prefill();
    this._debug('bind:ready', this._debugContext());
  }

  _isDesignMode() {
    return this.dataset.designMode === 'true';
  }

  _showSummary(msg) {
    if (!this._summary) return;
    this._summary.textContent = msg;
    this._summary.hidden = false;
    this._summary.removeAttribute('hidden');
    this._summary.classList.add('fdb-ms-summary--visible');
    this._summary.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    this._summary.focus({ preventScroll: true });
  }

  _hideSummary() {
    if (!this._summary) return;
    this._summary.hidden = true;
    this._summary.setAttribute('hidden', '');
    this._summary.classList.remove('fdb-ms-summary--visible');
    this._summary.textContent = '';
  }

  _prefill() {
    this._setVal('firstName', this.dataset.customerFirst || '');
    this._setVal('lastName', this.dataset.customerLast || '');
    this._setVal('email', this.dataset.customerEmail || '');
    if (this.dataset.formMode === 'unsubscribe') return;
    let ph = this.dataset.customerPhone || '';
    if (ph.startsWith('+45')) ph = ph.slice(3);
    this._setVal('phone', ph.replace(/\s/g, ''));
  }

  _isUnsubscribe() {
    return this.dataset.formMode === 'unsubscribe';
  }

  _field(name) {
    return this.querySelector('[data-field="' + name + '"]');
  }

  _setVal(name, v) {
    const f = this._field(name);
    if (f) f.value = v;
  }

  _normPhone(raw) {
    const d = raw.replace(/\D/g, '');
    return d.length === 8 ? '+45' + d : null;
  }

  _normDate(dd, mm, yyyy) {
    if (!dd || !mm || !yyyy) return '';
    const d = parseInt(dd, 10);
    const m = parseInt(mm, 10);
    const y = parseInt(yyyy, 10);
    if (!d || !m || !y || m > 12 || d > 31 || y < 1900 || y > 2099) return null;
    const dt = new Date(y, m - 1, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
    return y + '-' + String(m).padStart(2, '0') + '-' + String(d).padStart(2, '0');
  }

  _errEl(input) {
    const id = input.getAttribute('aria-describedby');
    return id ? this.querySelector('#' + id) : null;
  }

  _setErr(input, msg) {
    if (!input) return;
    const el = this._errEl(input);
    if (el) {
      el.textContent = msg;
      el.hidden = false;
      el.removeAttribute('hidden');
    }
    input.setAttribute('aria-invalid', 'true');
    const field = input.closest('.fdb-ms-field');
    if (field) field.classList.add('fdb-ms-field--error');
  }

  _clearErr(input) {
    if (!input) return;
    const el = this._errEl(input);
    if (el) {
      el.textContent = '';
      el.hidden = true;
      el.setAttribute('hidden', '');
    }
    input.removeAttribute('aria-invalid');
    const field = input.closest('.fdb-ms-field');
    if (field) field.classList.remove('fdb-ms-field--error');
  }

  _applyServerFieldErrors(fields) {
    if (!fields || typeof fields !== 'object') return 0;
    const aliases = { birthday: 'birthday-dd' };
    let count = 0;
    for (const [name, msg] of Object.entries(fields)) {
      if (typeof msg !== 'string' || !msg.trim()) continue;
      const input = this._field(aliases[name] || name);
      if (!input) continue;
      this._setErr(input, msg.trim());
      count++;
    }
    return count;
  }

  _manyErrorsMsg(count) {
    const tmpl = this.dataset.msgErrn || 'Der er %d fejl i formularen. Ret dem og prøv igen.';
    return tmpl.replace('%d', count);
  }

  _resolveServerError(json, fieldErrorCount) {
    const custom = typeof json.error === 'string' ? json.error.trim() : '';
    if (custom) return custom;
    if (fieldErrorCount === 1) return this.dataset.msgErr1;
    if (fieldErrorCount > 1) return this._manyErrorsMsg(fieldErrorCount);
    return this.dataset.error || 'Noget gik galt. Prøv igen eller kontakt os.';
  }

  _requireField(name, msg, errors) {
    const input = this._field(name);
    if (!input) {
      errors.push(null);
      return null;
    }
    if (input.type === 'checkbox') {
      if (input.checked) {
        this._clearErr(input);
        return input;
      }
      this._setErr(input, msg);
      errors.push(input);
      return input;
    }
    if (input.value.trim()) {
      this._clearErr(input);
      return input;
    }
    this._setErr(input, msg);
    errors.push(input);
    return input;
  }

  _validate() {
    const hp = this.querySelector('[data-honeypot]');
    if (hp && hp.value && !this._isDesignMode()) {
      this._debug('validate:honeypot', { valueLength: hp.value.length });
      return { ok: false, honeypot: true };
    }

    const errors = [];

    const fn = this._requireField('firstName', this.dataset.msgFn, errors);
    const ln = this._requireField('lastName', this.dataset.msgLn, errors);

    const em = this._field('email');
    if (!em) {
      errors.push(null);
    } else if (!em.value.trim()) {
      this._setErr(em, this.dataset.msgEmReq);
      errors.push(em);
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em.value.trim())) {
      this._setErr(em, this.dataset.msgEmInv);
      errors.push(em);
    } else {
      this._clearErr(em);
    }

    if (this._isUnsubscribe()) {
      const cleanErrors = errors.filter(Boolean);
      if (cleanErrors.length) {
        this._debug('validate:failed', {
          errorCount: cleanErrors.length,
          fields: cleanErrors.map((input) => this._fieldLabel(input))
        });
        return { ok: false, errors: cleanErrors };
      }
      const data = {
        formType: 'unsubscribe',
        firstName: fn ? fn.value.trim() : '',
        lastName: ln ? ln.value.trim() : '',
        email: em ? em.value.trim() : '',
        honeypot: ''
      };
      this._debug('validate:ok', { payload: data });
      return { ok: true, data };
    }

    const ph = this._field('phone');
    let phNorm = null;
    if (!ph) {
      errors.push(null);
    } else if (!ph.value.trim()) {
      this._setErr(ph, this.dataset.msgPhReq);
      errors.push(ph);
    } else {
      phNorm = this._normPhone(ph.value || '');
      if (!phNorm) {
        this._setErr(ph, this.dataset.msgPhDig);
        errors.push(ph);
      } else {
        this._clearErr(ph);
      }
    }

    const pn = this._field('postnummer');
    if (!pn) {
      errors.push(null);
    } else if (/^\d{4}$/.test(pn.value)) {
      this._clearErr(pn);
    } else {
      this._setErr(pn, this.dataset.msgPn);
      errors.push(pn);
    }

    const dd = this._field('birthday-dd');
    const mm = this._field('birthday-mm');
    const yy = this._field('birthday-yyyy');
    let birthday = '';
    if (!dd || !mm || !yy) {
      errors.push(null);
    } else {
      const bdParts = [dd.value.trim(), mm.value.trim(), yy.value.trim()];
      if (!bdParts.every(Boolean)) {
        this._setErr(dd, this.dataset.msgBdReq);
        errors.push(dd);
      } else {
        const res = this._normDate(bdParts[0], bdParts[1], bdParts[2]);
        if (res === null) {
          this._setErr(dd, this.dataset.msgBd);
          errors.push(dd);
        } else {
          birthday = res;
          this._clearErr(dd);
        }
      }
    }

    this._requireField('membershipconsent', this.dataset.msgMembershipCn, errors);
    this._requireField('marketingconsent', this.dataset.msgMarketingCn, errors);
    this._requireField('adsharedataconsent', this.dataset.msgAdshareCn, errors);

    const smsConsent = this._field('smsconsent');
    if (smsConsent) this._clearErr(smsConsent);

    const cleanErrors = errors.filter(Boolean);
    if (cleanErrors.length) {
      this._debug('validate:failed', {
        errorCount: cleanErrors.length,
        fields: cleanErrors.map((input) => this._fieldLabel(input))
      });
      return { ok: false, errors: cleanErrors };
    }

    const data = {
      formType: 'signup',
      firstName: fn.value.trim(),
      lastName: ln.value.trim(),
      email: em.value.trim(),
      phone: phNorm,
      birthday: birthday,
      postnummer: pn.value.trim(),
      membershipconsent: true,
      // Field 2 (the marketing_consent checkbox) is one legal consent that
      // fans out to emailconsent + personalizationconsent. The N8N contract
      // uses those keys, not marketingconsent. Both are hardcoded true because
      // the checkbox is required, matching membership/adsharedataconsent.
      emailconsent: true,
      personalizationconsent: true,
      adsharedataconsent: true,
      smsconsent: Boolean(smsConsent && smsConsent.checked),
      honeypot: ''
    };
    this._debug('validate:ok', { payload: data });
    return { ok: true, data };
  }

  async _onSubmit(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    this._debug('submit:start', { eventType: e ? e.type : 'programmatic' });

    if (this._submitting) {
      this._debug('submit:ignored', { reason: 'already-submitting' });
      return;
    }
    this._hideSummary();

    const webhook = (this.dataset.webhook || '').trim();
    if (!webhook) {
      this._debug('submit:no-webhook');
      this._showSummary(this.dataset.msgNoWebhook || this.dataset.error || 'Formularen er ikke konfigureret.');
      return;
    }

    let result;
    try {
      result = this._validate();
    } catch (err) {
      this._debug('validate:exception', { message: err && err.message ? err.message : String(err) });
      this._showSummary(this.dataset.error || 'Noget gik galt. Prøv igen eller kontakt os.');
      return;
    }

    if (result.honeypot) {
      this._debug('submit:stopped', { reason: 'honeypot' });
      return;
    }

    if (!result.ok) {
      const n = result.errors.length;
      this._showSummary(n === 1
        ? this.dataset.msgErr1
        : this._manyErrorsMsg(n));
      const focusTarget = result.errors.find((el) => el && typeof el.focus === 'function');
      if (focusTarget) focusTarget.focus();
      return;
    }

    const origLabel = this._btnText.textContent;
    this._submitting = true;
    this._btn.disabled = true;
    this._btnText.textContent = this.dataset.msgLoad || 'Sender...';

    this._debug('fetch:start', {
      url: webhook,
      payload: result.data,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    try {
      const res = await fetch(webhook, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(result.data)
      });

      let json = {};
      let parseError = null;
      try {
        json = await res.json();
      } catch (err) {
        parseError = err && err.message ? err.message : String(err);
      }

      this._debug('fetch:response', {
        ok: res.ok,
        status: res.status,
        statusText: res.statusText,
        json,
        parseError
      });

      if (res.ok && json.ok !== false) {
        const successUrl = (this.dataset.successUrl || '').trim();
        if (successUrl && !this._isDesignMode()) {
          this._redirecting = true;
          this._debug('submit:redirect', { successUrl });
          window.location.assign(successUrl);
          return;
        }
        if (this._successPanel) {
          this._form.hidden = true;
          this._successPanel.hidden = false;
          this._successPanel.removeAttribute('hidden');
          this._successPanel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          this._successPanel.focus();
        } else {
          this._showSummary(this.dataset.msgSuccess || 'Tak! Vi har modtaget din tilmelding.');
        }
        this._debug('submit:success-panel', { hadPanel: Boolean(this._successPanel) });
        return;
      }

      const fieldErrorCount = this._applyServerFieldErrors(json.fields);
      const summaryMsg = this._resolveServerError(json, fieldErrorCount);
      this._showSummary(summaryMsg);
      const focusTarget = fieldErrorCount
        ? Object.keys(json.fields || {})
            .map((name) => this._field(name === 'birthday' ? 'birthday-dd' : name))
            .find((el) => el && typeof el.focus === 'function')
        : null;
      if (focusTarget) focusTarget.focus();
      this._debug('submit:server-error', { status: res.status, json, fieldErrorCount, summaryMsg });
      return;
    } catch (err) {
      this._debug('fetch:error', { message: err && err.message ? err.message : String(err) });
      this._showSummary(this.dataset.error || 'Noget gik galt. Prøv igen eller kontakt os.');
    } finally {
      if (!this._redirecting) {
        this._submitting = false;
        this._btn.disabled = false;
        this._btnText.textContent = origLabel;
        this._debug('submit:finished');
      }
    }
  }
}

if (!window.customElements.get('fdb-member-signup')) {
  window.customElements.define('fdb-member-signup', FdbMemberSignup);
}
